import React, { useState, useEffect } from "react";

export const Skeleton = ({ className, style }) => {
  return (
    <div
      className={`premium-skeleton ${className || ""}`}
      style={style}
    />
  );
};

export const ImageWithSkeleton = ({
  src,
  alt,
  className,
  skeletonClassName,
  style,
  onError,
  fallbackSrc,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = React.useRef(null);

  // Reset loading state if src changes
  useEffect(() => {
    if (src) {
      setIsLoaded(false);
      setHasError(false);
    }
  }, [src]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setIsLoaded(true);
    }
  }, [src]);

  return (
    <div className={`relative overflow-hidden ${className || ""}`} style={style}>
      {/* Skeleton overlay - Fades out when image loads */}
      <div
        className={`absolute inset-0 z-10 transition-opacity duration-500 ease-in-out ${
          isLoaded || hasError ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <Skeleton className={`w-full h-full ${skeletonClassName || ""}`} />
      </div>

      {/* Actual Image */}
      {src && !hasError ? (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`px-4 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setIsLoaded(true)}
          onError={(e) => {
            setHasError(true);
            if (onError) onError(e);
          }}
        />
      ) : (
        <img
          src={fallbackSrc || "/images/Product.webp"}
          alt={alt || "Item"}
          className="w-full h-full object-contain opacity-100"
        />
      )}
    </div>
  );
};

export const ProductSkeletonCard = () => (
  <div
    className="glass-card rounded-lg overflow-hidden flex flex-col group relative"
    style={{
      border: "1.5px solid rgba(34,197,94,0.15)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.05)"
    }}
  >
    <div className="relative flex justify-center mt-1 px-1">
      <div className="w-full aspect-[1.25/1] overflow-hidden rounded-lg relative">
        <Skeleton className="w-full h-full rounded-lg" />
      </div>
    </div>
    <div className="flex items-center justify-between px-3 pt-2.5">
      <Skeleton className="w-24 h-4 rounded-md" />
      <Skeleton className="w-4 h-4 rounded-sm" />
    </div>
    <div className="flex justify-between items-center px-2 py-3 mt-auto">
      <Skeleton className="w-12 h-5 rounded-md" />
      <Skeleton className="w-16 h-8 rounded-md" />
    </div>
  </div>
);


export const CartSkeleton = () => (
  <div className="flex flex-col gap-3 px-4 pt-4">
    {[1, 2, 3].map(i => (
      <div key={i} className="py-3 border-b border-gray-150 flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Skeleton className="w-3.5 h-3.5 rounded-sm" />
            <Skeleton className="w-32 h-4 rounded-md" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-20 h-7 rounded-lg" />
            <Skeleton className="w-12 h-4 rounded-md" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-1">
          <Skeleton className="w-12 h-4 rounded-md" />
          <Skeleton className="w-4 h-4 rounded-sm" />
        </div>
      </div>
    ))}
  </div>
);

export const CartSummarySkeleton = () => (
  <div className="bg-white mt-2 px-4 py-4 mb-[200px]">
    <Skeleton className="w-32 h-5 rounded-md mb-4" />
    <div className="flex justify-between mb-2">
      <Skeleton className="w-24 h-4 rounded-md" />
      <Skeleton className="w-16 h-4 rounded-md" />
    </div>
    <div className="flex justify-between mb-2">
      <Skeleton className="w-24 h-4 rounded-md" />
      <Skeleton className="w-16 h-4 rounded-md" />
    </div>
    <div className="flex justify-between mt-3 pt-3 border-t border-gray-200">
      <Skeleton className="w-24 h-5 rounded-md" />
      <Skeleton className="w-20 h-5 rounded-md" />
    </div>
  </div>
);


export const ProfileSkeleton = () => (
  <div className="w-11/12 sm:w-19/20 md:w-9/10 lg:w-5/6 flex flex-col items-center mt-6 relative z-10 mx-auto">
    <Skeleton className="w-28 h-28 rounded-full mb-4" />
    <Skeleton className="w-40 h-6 rounded-md mb-2" />
    <Skeleton className="w-32 h-4 rounded-md mb-8" />
    
    <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-2 flex flex-col gap-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="flex flex-col gap-2">
              <Skeleton className="w-32 h-4 rounded-md" />
              <Skeleton className="w-48 h-3 rounded-md" />
            </div>
          </div>
          <Skeleton className="w-6 h-6 rounded-full" />
        </div>
      ))}
    </div>
  </div>
);


export const OrderSkeleton = () => (
  <div className="flex flex-col gap-4 w-full px-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 w-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="w-24 h-4 rounded-md" />
            <Skeleton className="w-32 h-3 rounded-md" />
          </div>
          <Skeleton className="w-20 h-6 rounded-full" />
        </div>
        {/* Items */}
        <div className="flex flex-col gap-3 py-3 border-y border-gray-100 mb-3">
          {[1, 2].map((j) => (
            <div key={j} className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Skeleton className="w-4 h-4 rounded-sm" />
                <Skeleton className="w-32 h-4 rounded-md" />
              </div>
              <Skeleton className="w-12 h-4 rounded-md" />
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="flex justify-between items-center">
          <Skeleton className="w-24 h-5 rounded-md" />
          <Skeleton className="w-20 h-8 rounded-lg" />
        </div>
      </div>
    ))}
  </div>
);


export const SkeletonLoader = () => (
  <div className="w-full min-h-[80vh] px-4 pt-20 pb-24">
    {/* Search/Header Bar Skeleton */}
    <Skeleton className="w-full h-12 rounded-xl mb-6" />
    
    {/* Category Slider Skeleton */}
    <div className="flex gap-3 overflow-hidden mb-8">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="flex-shrink-0 w-20 h-24 rounded-2xl" />
      ))}
    </div>
    
    {/* Section Title Skeleton */}
    <Skeleton className="w-48 h-7 rounded-md mb-5 mx-auto" />
    
    {/* Grid Items Skeleton */}
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="glass-card rounded-lg border border-gray-100/50 p-2 flex flex-col shadow-sm">
          <Skeleton className="w-full aspect-[4/3] rounded-lg mb-3" />
          <Skeleton className="w-3/4 h-4 rounded mb-2 mx-1" />
          <Skeleton className="w-1/2 h-4 rounded mb-4 mx-1" />
          <div className="flex justify-between items-center mt-auto px-1 pb-1">
            <Skeleton className="w-12 h-5 rounded" />
            <Skeleton className="w-16 h-8 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

